import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
} from 'recharts';

import { MetricChartTooltip, MetricChartTooltipProps } from '../metric-chart-tooltip.component';
import {
  getTicksSettings,
  defaultLegendFormatter,
  numberFormatter,
  moneyFormatter,
} from '../metric-chart.utils';
import {
  DEFAULT_BAR_COLOR,
  DEFAULT_BAR_WIDTH,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_TICKS_COUNT,
  LEGEND_HEIGHT_OFFSET,
  NO_DATA_LABEL,
  tickFormatterMap,
} from '../metric-chart.constants';
import { MetricChartTicks, MetricType, MetricTimePoint } from '../metric-chart.types';
import { MetricChartLegend } from '../metric-chart-legend.component';

export type MetricBarChartProps = {
  metrics: MetricTimePoint[];
  metricsType?: MetricType;
  noDataLabel?: string;
  height?: number;
  barColor?: string | string[];
  barWidth?: number;
  isBarStacked?: boolean;
  tickCount?: MetricChartTicks;
  isLegendVisible?: boolean;
  legendFormatter?: (value: string) => string;
  tooltipContent?: MetricChartTooltipProps['tooltipContent'] | null;
  xTicksFormatter?: (time: number) => string;
  centsThreshold?: number;
};

export const MetricBarChart = ({
  metrics,
  metricsType = 'number',
  height = DEFAULT_CHART_HEIGHT,
  noDataLabel = NO_DATA_LABEL,
  barColor = DEFAULT_BAR_COLOR,
  barWidth = DEFAULT_BAR_WIDTH,
  isBarStacked,
  tickCount = DEFAULT_TICKS_COUNT,
  isLegendVisible,
  legendFormatter = defaultLegendFormatter,
  tooltipContent,
  xTicksFormatter,
  centsThreshold,
}: MetricBarChartProps) => {
  const theme = useTheme();

  const data = useMemo(
    () => metrics.map((metric) => ({ date: metric.date.getTime(), value: metric.value })),
    [metrics],
  );

  const gridColor = alpha(theme.palette.text.primary, 0.12);
  const tickStyle = { fontSize: '10px', fill: theme.palette.text.secondary };
  const {
    ticks,
    ticksFormatter: defaultTicksFormatter,
    ticksShift,
    keys,
  } = getTicksSettings(data, {
    minTicksCount: tickCount,
  });

  const ticksFormatter = xTicksFormatter || defaultTicksFormatter;
  const currencyFormatter = useMemo(
    () => (value: number) => moneyFormatter(value, centsThreshold),
    [centsThreshold],
  );

  return (
    <Box
      alignContent='center'
      sx={{
        userSelect: 'none',
        width: '100%',
        height: height + (isLegendVisible && metrics.length ? LEGEND_HEIGHT_OFFSET : 0),
      }}
    >
      {!metrics.length ? (
        <Typography my={2} textAlign='center' color='text.secondary'>
          {noDataLabel}
        </Typography>
      ) : (
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={data} margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
            <CartesianGrid stroke={gridColor} vertical={false} />
            <XAxis
              dataKey='date'
              type='number'
              scale='time'
              domain={[(min: number) => min - ticksShift, (max: number) => max + ticksShift]}
              ticks={ticks}
              tickFormatter={ticksFormatter}
              tick={tickStyle}
              tickMargin={8}
              interval='preserveStartEnd'
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={tickFormatterMap[metricsType]}
              tick={tickStyle}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={24}
              {...(metricsType === 'time'
                ? { domain: [0, 24 * 60], ticks: [0, 6 * 60, 12 * 60, 18 * 60, 24 * 60] }
                : {})}
            />
            {tooltipContent !== null && (
              <Tooltip
                position={{ x: 0, y: 0 }}
                cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                wrapperStyle={{ pointerEvents: 'none' }}
                content={
                  <MetricChartTooltip
                    tooltipContent={tooltipContent}
                    legendFormatter={legendFormatter}
                    ticksFormatter={ticksFormatter}
                    valueFormatter={
                      metricsType === 'currency' ? currencyFormatter : numberFormatter
                    }
                  />
                }
              />
            )}
            {isLegendVisible && (
              <Legend content={<MetricChartLegend />} formatter={legendFormatter} />
            )}
            {keys.map((key, index) => {
              const color = Array.isArray(barColor) ? barColor[index] : barColor;
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={isBarStacked ? 'a' : key}
                  fill={color || DEFAULT_BAR_COLOR}
                  maxBarSize={barWidth}
                  radius={isBarStacked && index < keys.length - 1 ? [0, 0, 0, 0] : [2, 2, 0, 0]}
                  isAnimationActive={false}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
